import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { ApiBaseUrl } from "../../utils/Constants";
import { useDispatch, useSelector } from "react-redux";
import {
  saveAssessmentSession,
  getAssessmentSessions,
  loadAssessmentSession,
  assessmentSessionChat,
  appendChatMessage,
  clearCurrentSession,
} from "../../redux/AssessmentSessionSlice";
import "./QuickAssessPanel.scss";
import FormatResponse from "../YourAi/FormatResponse";
import { INTAKE_QUESTIONS } from "../IntakeForm/intakeQuestions";
import AssessmentPdfDrawer from "../PdfViewer/AssessmentPdfDrawer";

// ── Helpers ───────────────────────────────────────────────────────────────────
function serializeFormAnswers(questions, answers) {
  const lines = [];
  questions.forEach((q) => {
    if (q.conditional) {
      const parentAnswer = answers[`q_${q.conditional.questionId}`];
      const parentSelected = Array.isArray(parentAnswer) ? parentAnswer : [parentAnswer];
      if (!parentSelected.some((v) => q.conditional.triggerValues.includes(v))) return;
    }
    const answer = answers[`q_${q.id}`];
    if (!answer && answer !== 0) return;

    if (q.answerType === "text-input" && q.fields) {
      lines.push(`${q.text}`);
      q.fields.forEach((field, i) => {
        const val = answer?.[i] || "";
        if (val) lines.push(`  ${field}: ${val}`);
      });
    } else if (q.answerType === "checkbox") {
      const selected = Array.isArray(answer) ? answer : [];
      if (selected.length > 0) {
        lines.push(`${q.text}`);
        lines.push(`  ${selected.join(", ")}`);
        if (q.subQuestion && selected.some((v) => q.subQuestion.triggerValues.includes(v))) {
          const sub = answers[`q_${q.id}_sub`];
          if (sub) lines.push(`  Detail: ${sub}`);
        }
      }
    } else {
      lines.push(`${q.text}`);
      lines.push(`  ${Array.isArray(answer) ? answer.join(", ") : answer}`);
      if (q.subQuestion && q.subQuestion.triggerValues.includes(Array.isArray(answer) ? answer[0] : answer)) {
        const sub = answers[`q_${q.id}_sub`];
        if (sub) lines.push(`  Detail: ${sub}`);
      }
    }
  });
  return lines.join("\n");
}

async function uploadFilesToBackend(surveyFile, bloodTestFile) {
  const formData = new FormData();
  if (surveyFile) formData.append("surveyPdf", surveyFile);
  if (bloodTestFile) formData.append("bloodTestPdf", bloodTestFile);
  const response = await axios.post(ApiBaseUrl + "/v1/user/quick-assess/upload", formData, {
    headers: { Authorization: localStorage.getItem("token"), "Content-Type": "multipart/form-data" },
  });
  if (response.data?.status !== 1) throw new Error(response.data?.message || "File upload failed");
  return response.data.files;
}

// ── Save Modal ────────────────────────────────────────────────────────────────
function SaveModal({ onSave, onCancel, isSaving }) {
  const [name, setName] = useState("");
  const inputRef = useRef();
  useEffect(() => { inputRef.current?.focus(); }, []);
  const handleSave = () => { if (name.trim()) onSave(name.trim()); };
  return (
    <div className="qa-modal-backdrop">
      <div className="qa-modal">
        <h4 className="qa-modal__title">Save Assessment Session</h4>
        <p className="qa-modal__sub">Give this assessment a name so you can return to it later and continue asking questions.</p>
        <input ref={inputRef} className="qa-modal__input" type="text" placeholder="e.g. John D — TRT Assessment, May 2025"
          value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSave()} maxLength={80} />
        <div className="qa-modal__actions">
          <button className="qa-btn qa-btn--secondary qa-modal__cancel" onClick={onCancel}>Cancel</button>
          <button className="qa-btn qa-btn--save qa-modal__save" onClick={handleSave} disabled={!name.trim() || isSaving}>
            {isSaving ? <span className="qa-btn__loading"><span className="qa-spinner" /> Saving...</span> : "Save Session"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Uploaded Files Panel ──────────────────────────────────────────────────────
function UploadedFilesPanel({ uploadedFileMeta }) {
  if (!uploadedFileMeta) return null;
  const { surveyUrl, surveyName, bloodTestUrl, bloodTestName } = uploadedFileMeta;
  const files = [
    surveyUrl && { url: surveyUrl, name: surveyName || "Patient Survey", label: "Patient Survey", icon: "📋" },
    bloodTestUrl && { url: bloodTestUrl, name: bloodTestName || "Blood Test Results", label: "Blood Test Results", icon: "🩸" },
  ].filter(Boolean);
  if (files.length === 0) return null;
  const openFile = (url) => { const fullUrl = url.startsWith("http") ? url : ApiBaseUrl + url; window.open(fullUrl, "_blank", "noopener,noreferrer"); };
  return (
    <details className="qa-files-panel">
      <summary className="qa-session-summary__toggle">Uploaded Documents</summary>
      <div className="qa-files-panel__list">
        {files.map(({ url, name, label, icon }) => (
          <button key={label} className="qa-file-item" onClick={() => openFile(url)} title={`Open ${name} in new tab`}>
            <span className="qa-file-item__icon">{icon}</span>
            <div className="qa-file-item__info">
              <span className="qa-file-item__label">{label}</span>
              <span className="qa-file-item__name">{name}</span>
            </div>
          </button>
        ))}
      </div>
    </details>
  );
}

// ── Doctor Notes Panel ────────────────────────────────────────────────────────
// Shows the doctor's clinical context notes (read-only preview after submission)
function DoctorNotesPanel({ doctorContext }) {
  if (!doctorContext || !doctorContext.trim()) return null;
  return (
    <details className="qa-form-summary-panel">
      {/* <summary className="qa-session-summary__toggle">Doctor's Clinical Notes</summary> */}
      <summary className="qa-session-summary__toggle">Doctor's Clinical Notes</summary>
      <div className="qa-doctor-notes-panel__body">
        <p className="qa-doctor-notes-panel__text">{doctorContext}</p>
      </div>
    </details>
  );
}
// ── Decision Sources Panel ────────────────────────────────────────────────────
// Shows WHY the AI said approved/eligible or rejected/not-eligible.
// Appears in both the live result view and the saved session view.
//
// Props:
//   reviewId      {string}   — ClinicalReview _id (to fetch full report from backend)
//   assessment    {object}   — live assessment result (from quickAssess response)
//   uploadedFiles {object}   — { surveyUrl, bloodTestUrl } for "View in PDF" buttons
//   onOpenPdf     {function} — (url, excerpt, verbatimExcerpt) => void
function DecisionSourcesPanel({ reviewId, assessment, uploadedFiles, onOpenPdf, apiBaseUrl }) {
  const [expanded, setExpanded] = React.useState(false);
  const [report, setReport] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const ApiBaseUrl = apiBaseUrl || require("../../utils/Constants").ApiBaseUrl;

  // Use live assessment data if available, otherwise fetch from backend
  const sources = report?.sources || assessment?.sources || [];
  const verdict = report?.decisionVerdict || assessment?.verdict || "";
  const keyFindings = report?.keyFindings || assessment?.keyFindings || [];
  const cautions = report?.cautions || assessment?.cautions || [];
  const hardStops = report?.hardStops || [];
  const eligible = report?.eligible ?? assessment?.eligible;
  // Protocol rules only come from the backend report (not from the live assessment object)
  const matchedRules = report?.matchedRules || assessment?.matchedRules || [];
  const protocolRef = report?.protocolReference || null;

  const fetchReport = async () => {
    if (report || !reviewId) return; // already loaded or no reviewId
    setLoading(true);
    setError("");
    try {
      const { default: axios } = await import("axios");
      const res = await axios.get(
        `${ApiBaseUrl}/v1/clinical/review/${reviewId}/decision-sources`,
        { headers: { Authorization: localStorage.getItem("token") } }
      );
      if (res.data?.status === 1) {
        setReport(res.data.data);
      } else {
        setError(res.data?.message || "Could not load decision sources.");
      }
    } catch (e) {
      setError("Network error loading decision sources.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) fetchReport();
  };

  const SOURCE_ICONS = {
    "patient survey": "📋",
    "blood test results": "🩸",
    "doctor context": "👨‍⚕️",
    "bobbi formulary": "📑",
  };

  const getSourceIcon = (label = "") => {
    const key = label.toLowerCase();
    for (const [k, v] of Object.entries(SOURCE_ICONS)) {
      if (key.includes(k.split(" ")[0])) return v;
    }
    return "📎";
  };

  return (
    <div className={`qa-decision-sources ${eligible ? "qa-decision-sources--approved" : "qa-decision-sources--rejected"}`}>
      <button className="qa-decision-sources__toggle" onClick={handleToggle}>
        <span className="qa-decision-sources__toggle-icon">
          {eligible ? "✅" : "❌"}
        </span>
        <span className="qa-decision-sources__toggle-label">
          Why was this assessment {eligible ? "approved" : "rejected"}?
        </span>
        <span className="qa-decision-sources__caret">{expanded ? "▲" : "▼"}</span>
      </button>

      {expanded && (
        <div className="qa-decision-sources__body">
          {loading && (
            <div className="qa-decision-sources__loading">
              <span className="qa-spinner qa-spinner--blue" /> Loading decision report...
            </div>
          )}
          {error && <p className="qa-decision-sources__error">{error}</p>}

          {!loading && (
            <>
              {/* Verdict */}
              {verdict && (
                <div className="qa-decision-sources__verdict-block">
                  <span className="qa-decision-sources__section-label">AI Verdict</span>
                  <p className="qa-decision-sources__verdict-text">{verdict}</p>
                </div>
              )}

              {/* Hard stops (absolute contraindications) */}
              {hardStops.length > 0 && (
                <div className="qa-decision-sources__section">
                  <span className="qa-decision-sources__section-label qa-decision-sources__section-label--stop">
                    🚫 Absolute Contraindications (Hard Stops)
                  </span>
                  <ul className="qa-decision-sources__list qa-decision-sources__list--stop">
                    {hardStops.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </div>
              )}

              {/* Cautions */}
              {cautions.length > 0 && (
                <div className="qa-decision-sources__section">
                  <span className="qa-decision-sources__section-label qa-decision-sources__section-label--warn">
                    ⚠ Cautions Identified
                  </span>
                  <ul className="qa-decision-sources__list qa-decision-sources__list--warn">
                    {cautions.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                </div>
              )}

              {/* Key findings */}
              {keyFindings.length > 0 && (
                <div className="qa-decision-sources__section">
                  <span className="qa-decision-sources__section-label">📊 Key Clinical Findings Used</span>
                  <ul className="qa-decision-sources__list">
                    {keyFindings.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                </div>
              )}

              {/* ── Clinical Protocol Rules Checked ──────────────────────────── */}
              {matchedRules.length > 0 && (
                <div className="qa-decision-sources__section">
                  <span className="qa-decision-sources__section-label">📋 Clinical Protocol Rules Applied</span>
                  {protocolRef && (
                    <div className="qa-protocol-ref-badge">
                      <span className="qa-protocol-ref-badge__icon">📄</span>
                      <div className="qa-protocol-ref-badge__content">
                        <div className="qa-protocol-ref-badge__top">
                          <span className="qa-protocol-ref-badge__name">{protocolRef.docName}</span>
                          {/* <span className="qa-protocol-ref-badge__file">{protocolRef.fileName}</span> */}
                          {protocolRef.pdfUrl && onOpenPdf && (
                            <button
                              className="qa-protocol-ref-badge__pdf-btn"
                              onClick={() => onOpenPdf(
                                protocolRef.pdfUrl.startsWith("http")
                                  ? protocolRef.pdfUrl
                                  : `${ApiBaseUrl}${protocolRef.pdfUrl}`,
                                null,
                                null
                              )}
                              title="Open full protocol PDF"
                            >
                              📖 Open Full Protocol PDF
                            </button>
                          )}
                        </div>
                        <p className="qa-protocol-ref-badge__desc">{protocolRef.description}</p>
                      </div>
                    </div>
                  )}
                  <div className="qa-rules-list">
                    {matchedRules.map((r, i) => {
                      const resultLower = (r.result || "").toLowerCase();
                      const isPassed = resultLower.includes("pass");
                      const isFailed = resultLower.includes("fail");
                      const isCaution = resultLower.includes("caution");
                      const protocolPdfUrl = protocolRef?.pdfUrl
                        ? (protocolRef.pdfUrl.startsWith("http")
                            ? protocolRef.pdfUrl
                            : `${ApiBaseUrl}${protocolRef.pdfUrl}`)
                        : null;
                      return (
                        <div key={i} className={`qa-rule-card ${isPassed ? "qa-rule-card--pass" : isFailed ? "qa-rule-card--fail" : "qa-rule-card--caution"}`}>
                          <div className="qa-rule-card__header">
                            <span className="qa-rule-card__badge">
                              {isPassed ? "✓ PASSED" : isFailed ? "✕ FAILED" : "⚠ CAUTION"}
                            </span>
                            {protocolPdfUrl && onOpenPdf && r.rule && (
                              <button
                                className="qa-rule-card__pdf-btn"
                                onClick={() => onOpenPdf(protocolPdfUrl, r.rule, r.rule)}
                                title="View this rule in the protocol PDF"
                              >
                                📄 View in Protocol PDF
                              </button>
                            )}
                          </div>
                          <p className="qa-rule-card__rule">"{r.rule}"</p>
                          <p className="qa-rule-card__reason">{r.reason}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Source excerpts — the actual text that drove the decision */}
              {sources.length > 0 && (
                <div className="qa-decision-sources__section">
                  <span className="qa-decision-sources__section-label">📎 Evidence From Source Documents</span>
                  <p className="qa-decision-sources__sources-hint">
                    These are the exact passages from the patient's documents that the AI used to make its decision.
                  </p>
                  <div className="qa-decision-sources__sources-list">
                    {sources.map((src, i) => {
                      const pdfUrl = resolvePdfUrl(src.label, uploadedFiles);
                      const canOpen = !!pdfUrl && !!src.excerpt && !!onOpenPdf;
                      return (
                        <div key={i} className="qa-decision-source-card">
                          <div className="qa-decision-source-card__header">
                            <span className="qa-decision-source-card__icon">{getSourceIcon(src.label)}</span>
                            <span className="qa-decision-source-card__label">{src.label}</span>
                            {canOpen && (
                              <button
                                className="qa-decision-source-card__pdf-btn"
                                onClick={() => onOpenPdf(pdfUrl, src.excerpt, src.verbatim_excerpt)}
                                title={`View this passage in the ${src.label} PDF`}
                              >
                                📄 View in PDF
                              </button>
                            )}
                          </div>
                          {src.excerpt && (
                            <blockquote className="qa-decision-source-card__excerpt">
                              "{src.excerpt}"
                            </blockquote>
                          )}
                          {src.verbatim_excerpt && src.verbatim_excerpt !== src.excerpt && (
                            <p className="qa-decision-source-card__verbatim">
                              <span className="qa-decision-source-card__verbatim-label">Matched text: </span>
                              {src.verbatim_excerpt}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {sources.length === 0 && !loading && (
                <p className="qa-decision-sources__empty">
                  Source references are available for assessments run after this feature was enabled.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── References / Sources Panel ────────────────────────────────────────────────
/**
 * Props:
 *   sources        {Array}    — array of { label, excerpt }
 *   uploadedFiles  {Object}   — { surveyUrl, bloodTestUrl, ... } from backend
 *   onOpenPdf      {Function} — (pdfUrl, excerpt, verbatimExcerpt) => void  — opens the drawer
 */
// Resolves a source label → full PDF URL. Used by both ReferencesPanel and MedCard.
function resolvePdfUrl(label = "", uploadedFiles = null) {
  if (!uploadedFiles) return null;
  const l = label.toLowerCase();
  if (l.includes("survey") || l.includes("patient")) {
    return uploadedFiles.surveyUrl
      ? (uploadedFiles.surveyUrl.startsWith("http")
          ? uploadedFiles.surveyUrl
          : ApiBaseUrl + uploadedFiles.surveyUrl)
      : null;
  }
  if (l.includes("blood") || l.includes("test") || l.includes("result")) {
    return uploadedFiles.bloodTestUrl
      ? (uploadedFiles.bloodTestUrl.startsWith("http")
          ? uploadedFiles.bloodTestUrl
          : ApiBaseUrl + uploadedFiles.bloodTestUrl)
      : null;
  }
  return null;
}

function ReferencesPanel({ sources, uploadedFiles, onOpenPdf }) {
  if (!sources || sources.length === 0) return null;

  return (
    <details className="qa-references-panel">
      <summary className="qa-references-panel__toggle">
        <span className="qa-references-panel__icon">📎</span>
        Reasoning &amp; Sources ({sources.length})
      </summary>
      <div className="qa-references-panel__body">
        {sources.map((src, i) => {
          const pdfUrl = resolvePdfUrl(src.label, uploadedFiles);
          const canOpen = !!pdfUrl && !!src.excerpt && !!onOpenPdf;
          return (
            <div className="qa-reference-item" key={i}>
              <div className="qa-reference-item__header">
                <span className="qa-reference-item__label">{src.label}</span>
                {canOpen && (
                  <button
                    className="qa-reference-item__pdf-btn"
                    onClick={() => onOpenPdf(pdfUrl, src.excerpt, src.verbatim_excerpt)}
                    title={`View in ${src.label} PDF`}
                  >
                    📄 View in PDF
                  </button>
                )}
              </div>
              {src.excerpt && <p className="qa-reference-item__excerpt">{src.excerpt}</p>}
            </div>
          );
        })}
      </div>
    </details>
  );
}

// Shows Q&A pairs after the intake form is submitted — mirrors the PDF panel style
function FilledFormSummaryPanel({ formAnswers, serviceType, onEdit }) {
  const questions = INTAKE_QUESTIONS[serviceType] || [];
  if (!formAnswers || Object.keys(formAnswers).length === 0) return null;

  const answered = [];
  questions.forEach((q) => {
    if (q.conditional) {
      const parentAnswer = formAnswers[`q_${q.conditional.questionId}`];
      const parentSelected = Array.isArray(parentAnswer) ? parentAnswer : [parentAnswer];
      if (!parentSelected.some((v) => q.conditional.triggerValues.includes(v))) return;
    }
    const answer = formAnswers[`q_${q.id}`];
    if (!answer && answer !== 0) return;

    let displayValue = "";
    if (q.answerType === "text-input" && q.fields) {
      displayValue = q.fields
        .map((field, i) => (answer?.[i] ? `${field}: ${answer[i]}` : null))
        .filter(Boolean)
        .join(" · ");
    } else if (q.answerType === "checkbox") {
      const selected = Array.isArray(answer) ? answer : [];
      displayValue = selected.join(", ");
      const sub = formAnswers[`q_${q.id}_sub`];
      if (sub && q.subQuestion && selected.some((v) => q.subQuestion.triggerValues.includes(v))) {
        displayValue += ` — ${sub}`;
      }
    } else {
      displayValue = Array.isArray(answer) ? answer.join(", ") : String(answer);
      const sub = formAnswers[`q_${q.id}_sub`];
      if (sub && q.subQuestion && q.subQuestion.triggerValues.includes(Array.isArray(answer) ? answer[0] : answer)) {
        displayValue += ` — ${sub}`;
      }
    }

    if (displayValue) answered.push({ question: q.text, answer: displayValue });
  });

  if (answered.length === 0) return null;

  return (
    <details className="qa-form-summary-panel" >
      <summary className="qa-session-summary__toggle">Survey Form</summary>
      {/* <summary className="qa-form-summary__toggle">
        <span className="qa-form-summary__icon">📝</span>
        <span className="qa-form-summary__label">Survey Form</span>
        <span className="qa-form-summary__badge">Form</span>
      </summary> */}
      <div className="qa-form-summary__body">
        {answered.map(({ question, answer }, i) => (
          <div key={i} className="qa-form-summary__row">
            <p className="qa-form-summary__q">{question}</p>
            <p className="qa-form-summary__a">{answer}</p>
          </div>
        ))}
        {onEdit && (
          <button className="qa-form-summary__edit-btn" onClick={onEdit}>
            ✎ Edit answers
          </button>
        )}
      </div>
    </details>
  );
}

// ── Intake Form Popup ─────────────────────────────────────────────────────────
// Reuses the IntakeForm modal shell (if-backdrop / if-modal / if-modal-header etc.)
// so it looks exactly like the existing clinical intake form popup.
function IntakeFormPopup({ serviceType, initialAnswers, onSubmit, onClose }) {
  const questions = INTAKE_QUESTIONS[serviceType] || [];
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const bodyRef = useRef();

  const visibleQuestions = questions.filter((q) => {
    if (!q.conditional) return true;
    const parentAnswer = answers[`q_${q.conditional.questionId}`];
    const parentSelected = Array.isArray(parentAnswer) ? parentAnswer : [parentAnswer];
    return parentSelected.some((v) => q.conditional.triggerValues.includes(v));
  });

  const totalVisible = visibleQuestions.length;
  const currentQ = visibleQuestions[currentQIndex];
  const progress = totalVisible > 0 ? Math.round(((currentQIndex + 1) / totalVisible) * 100) : 0;

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [currentQIndex]);

  const handleBackdrop = (e) => {
    if (e.target.classList.contains("if-backdrop")) onClose();
  };

  const setAnswer = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));

  const isSubVisible = (q) => {
    if (!q.subQuestion) return false;
    const answer = answers[`q_${q.id}`];
    if (!answer) return false;
    const selected = Array.isArray(answer) ? answer : [answer];
    return selected.some((v) => q.subQuestion.triggerValues.includes(v));
  };

  const goNext = () => { if (currentQIndex < totalVisible - 1) setCurrentQIndex((i) => i + 1); };
  const goPrev = () => { if (currentQIndex > 0) setCurrentQIndex((i) => i - 1); };

  const handleSubmit = () => {
    onSubmit(answers);
    setSubmitted(true);
  };

  const renderInput = (q) => {
    const ansKey = `q_${q.id}`;
    const answer = answers[ansKey];

    if (q.answerType === "radio") {
      return (
        <div className="if-radio-group">
          {q.options.map((opt) => (
            <label key={opt} className={`if-radio-label ${answer === opt ? "selected" : ""}`}>
              <input type="radio" name={ansKey} value={opt} checked={answer === opt}
                onChange={() => setAnswer(ansKey, opt)} />
              <span className="if-radio-custom" />
              {opt}
            </label>
          ))}
        </div>
      );
    }
    if (q.answerType === "checkbox") {
      const checked = Array.isArray(answer) ? answer : [];
      return (
        <div className="if-checkbox-group">
          {q.options.map((opt) => (
            <label key={opt} className={`if-checkbox-label ${checked.includes(opt) ? "selected" : ""}`}>
              <input type="checkbox" checked={checked.includes(opt)}
                onChange={() => {
                  const next = checked.includes(opt) ? checked.filter((v) => v !== opt) : [...checked, opt];
                  setAnswer(ansKey, next);
                }} />
              <span className="if-checkbox-custom" />
              {opt}
            </label>
          ))}
        </div>
      );
    }
    if (q.answerType === "textarea") {
      return (
        <textarea className="if-textarea" rows={4} placeholder={q.placeholder || "Please type your answer here…"}
          value={answer || ""} onChange={(e) => setAnswer(ansKey, e.target.value)} />
      );
    }
    if (q.answerType === "text-input" && q.fields) {
      return (
        <div className="if-text-input-group">
          {q.fields.map((field, fi) => (
            <div key={fi} className="if-text-input-row">
              <label className="if-field-label">{field}</label>
              <input type="text" className="if-text-input" placeholder={field}
                value={answer?.[fi] || ""}
                onChange={(e) => {
                  const prev = Array.isArray(answer) ? [...answer] : [];
                  prev[fi] = e.target.value;
                  setAnswer(ansKey, prev);
                }} />
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const serviceLabelFmt = (serviceType || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="if-backdrop" onClick={handleBackdrop}>
      <div className="if-modal">
        {/* Header — matches IntakeForm design exactly */}
        <div className="if-modal-header">
          <div className="if-header-left">
            <span className="if-badge">Clinical</span>
            <div>
              <h3 className="if-modal-title">Patient Intake Form</h3>
              <p className="if-modal-subtitle">{serviceLabelFmt}</p>
            </div>
          </div>
          <button className="if-close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div className="if-modal-body" ref={bodyRef}>
          {submitted ? (
            <div className="if-success">
              <div className="if-success-icon">✓</div>
              <h3>Intake Form Completed</h3>
              <p>Your answers have been captured and are ready for assessment.</p>
              <button className="if-btn if-btn-primary" onClick={onClose}>
                Continue to Assessment →
              </button>
            </div>
          ) : (
            <div className="if-wizard">
              <div className="if-wizard-top">
                <span className="if-service-tag">{serviceLabelFmt}</span>
              </div>

              {/* Progress bar */}
              <div className="if-progress-wrap">
                <div className="if-progress-bar">
                  <div className="if-progress-fill" style={{ width: `${progress}%` }} />
                </div>
                <span className="if-progress-label">{currentQIndex + 1} / {totalVisible}</span>
              </div>

              {currentQ && (
                <div className="if-question-card">
                  {currentQ.info?.trigger === "always" && (
                    <div className="if-info-banner">
                      <span className="if-info-icon">ℹ</span>
                      <p>{currentQ.info.message}</p>
                    </div>
                  )}
                  <p className="if-question-text">{currentQ.text}</p>
                  <div className="if-question-body">
                    {renderInput(currentQ)}
                  </div>
                  {currentQ.info && currentQ.info.trigger !== "always" && answers[`q_${currentQ.id}`] === currentQ.info.trigger && (
                    <div className="if-info-banner if-info-banner--warn">
                      <span className="if-info-icon">⚠</span>
                      <p>{currentQ.info.message}</p>
                    </div>
                  )}
                  {isSubVisible(currentQ) && currentQ.subQuestion?.answerType === "textarea" && (
                    <div className="if-subquestion">
                      <textarea className="if-textarea" rows={3}
                        placeholder={currentQ.subQuestion.placeholder || "Please provide details…"}
                        value={answers[`q_${currentQ.id}_sub`] || ""}
                        onChange={(e) => setAnswer(`q_${currentQ.id}_sub`, e.target.value)} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer nav */}
        {!submitted && (
          <div className="if-modal-footer">
            <button className="if-btn if-btn-ghost" onClick={goPrev} disabled={currentQIndex === 0}>
              ← Back
            </button>
            <div className="if-footer-right">
              {currentQIndex < totalVisible - 1 ? (
                <button className="if-btn if-btn-primary" onClick={goNext}>Next →</button>
              ) : (
                <button className="if-btn if-btn-submit" onClick={handleSubmit}>
                  Submit Form ✓
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Survey Input Section (left column) ────────────────────────────────────────
function SurveyInputSection({ surveyFile, setSurveyFile, surveyMode, setSurveyMode, formAnswers, setFormAnswers, serviceType, surveyRef }) {
  const [showIntakePopup, setShowIntakePopup] = useState(false);
  const answeredCount = Object.keys(formAnswers).filter((k) => !k.endsWith("_sub")).length;
  const formHasContent = answeredCount > 0;

  const handleIntakeSubmit = (newAnswers) => {
    setFormAnswers(newAnswers);
    setTimeout(() => setShowIntakePopup(false), 1200);
  };

  return (
    <>
      {showIntakePopup && (
        <IntakeFormPopup
          serviceType={serviceType}
          initialAnswers={formAnswers}
          onSubmit={handleIntakeSubmit}
          onClose={() => setShowIntakePopup(false)}
        />
      )}

      <div className="qa-survey-section">
        {surveyMode === "pdf" && (
          <>
            <div className={`qa-dropzone ${surveyFile ? "qa-dropzone--filled" : ""}`} onClick={() => surveyRef.current?.click()}>
              <input ref={surveyRef} type="file" accept=".pdf,.doc,.docx" style={{ display: "none" }}
                onChange={(e) => setSurveyFile(e.target.files[0] || null)} />
              {surveyFile ? (
                <>
                  <span className="qa-dropzone__check">✓</span>
                  <p className="qa-dropzone__name">{surveyFile.name}</p>
                  <button className="qa-dropzone__remove" onClick={(e) => { e.stopPropagation(); setSurveyFile(null); if (surveyRef.current) surveyRef.current.value = ""; }}>✕</button>
                </>
              ) : (
                <>
                  <span className="qa-dropzone__icon">📋</span>
                  <p className="qa-dropzone__title">Patient Survey PDF</p>
                  <p className="qa-dropzone__hint">Click to upload completed intake form / survey</p>
                </>
              )}
            </div>
            {!surveyFile && (
              <button className="qa-switch-btn" onClick={() => { setSurveyMode("form"); setShowIntakePopup(true); }}>
                <span className="qa-switch-btn__icon">📝</span>
                Fill intake form instead
              </button>
            )}
          </>
        )}

        {surveyMode === "form" && (
          <>
            {/* Clickable tile — opens the popup */}
            <div
              className={`qa-dropzone qa-dropzone--clickable ${formHasContent ? "qa-dropzone--form-filled" : "qa-dropzone--form-empty"}`}
              onClick={() => setShowIntakePopup(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setShowIntakePopup(true)}
            >
              {formHasContent ? (
                <>
                  <span className="qa-dropzone__check">✓</span>
                  <p className="qa-dropzone__name">Intake Form — {answeredCount} answers</p>
                  <p className="qa-dropzone__hint qa-dropzone__hint--green">Click to review / edit answers</p>
                </>
              ) : (
                <>
                  <span className="qa-dropzone__icon">📝</span>
                  <p className="qa-dropzone__title">Patient Intake Form</p>
                  <p className="qa-dropzone__hint">Click to open and fill in the form</p>
                </>
              )}
            </div>

            {/* Filled-form Q&A summary — shown below the tile once answers exist */}
            {formHasContent && (
              <FilledFormSummaryPanel
                formAnswers={formAnswers}
                serviceType={serviceType}
                onEdit={() => setShowIntakePopup(true)}
              />
            )}

            <button className="qa-switch-btn qa-switch-btn--back" onClick={() => { setSurveyMode("pdf"); setFormAnswers({}); }}>
              <span className="qa-switch-btn__icon">📄</span>
              Upload PDF instead
            </button>
          </>
        )}
      </div>
    </>
  );
}

// ── Session Chat View ─────────────────────────────────────────────────────────
function SessionChatView({ session }) {
  const dispatch = useDispatch();
  const { isChatLoading } = useSelector((s) => s.assessmentSessions);
  const [message, setMessage] = useState("");
  const [extraFile, setExtraFile] = useState(null);
  const [extraFileUploading, setExtraFileUploading] = useState(false);
  const fileRef = useRef();
  const chatEndRef = useRef();
  const chatHistory = session?.chatHistory || [];
  const assessment = session?.assessmentResult || {};

  // ── PDF Drawer state ──────────────────────────────────────────────────────
  const [pdfDrawerOpen, setPdfDrawerOpen] = useState(false);
  const [pdfDrawerUrl, setPdfDrawerUrl] = useState(null);
  const [pdfDrawerExcerpt, setPdfDrawerExcerpt] = useState(null);
  const [pdfDrawerVerbatim, setPdfDrawerVerbatim] = useState(null);

  const handleOpenPdf = (pdfUrl, excerpt, verbatimExcerpt) => {
    setPdfDrawerUrl(pdfUrl);
    setPdfDrawerExcerpt(excerpt);
    setPdfDrawerVerbatim(verbatimExcerpt || null);
    setPdfDrawerOpen(true);
  };
  const handleClosePdf = () => {
    setPdfDrawerOpen(false);
    setPdfDrawerUrl(null);
    setPdfDrawerExcerpt(null);
    setPdfDrawerVerbatim(null);
  };

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatHistory.length, isChatLoading]);

  const handleSend = async () => {
    if (!message.trim() && !extraFile) return;
    let additionalFileText = "";
    const fileCopy = extraFile;
    if (fileCopy) {
      setExtraFileUploading(true);
      try {
        const uploaded = await uploadFilesToBackend(null, fileCopy);
        additionalFileText = (uploaded.bloodTestPdf || uploaded.surveyPdf)?.text || "";
      } catch (e) { console.warn("Extra file upload failed:", e.message); }
      finally { setExtraFileUploading(false); setExtraFile(null); if (fileRef.current) fileRef.current.value = ""; }
    }
    const userMsg = message.trim() || `[Uploaded document: ${fileCopy?.name}]`;
    setMessage("");
    dispatch(appendChatMessage({ role: "user", content: userMsg, createdAt: new Date().toISOString() }));
    dispatch(assessmentSessionChat({ sessionId: session._id, message: message.trim() || "I've uploaded an additional document for your review.", additionalFileText }));
  };

  const svcLabel = (session?.serviceType || "general").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="qa-session-view">
      {/* ── PDF Reference Drawer (reused from AI Chat) ───────────────────── */}
      <AssessmentPdfDrawer
        isOpen={pdfDrawerOpen}
        onClose={handleClosePdf}
        pdfUrl={pdfDrawerUrl}
        excerpt={pdfDrawerExcerpt}
        verbatimExcerpt={pdfDrawerVerbatim}
      />

      <div className={`qa-verdict qa-verdict--${assessment.eligible ? "yes" : "no"} qa-verdict--compact`}>
        <span className="qa-verdict__icon">{assessment.eligible ? "✓" : "✕"}</span>
        <div className="qa-verdict__text">
          <span className="qa-verdict__label">{assessment.eligible ? "ELIGIBLE FOR TREATMENT" : "NOT ELIGIBLE"} — {svcLabel}</span>
          <p className="qa-verdict__reason">{assessment.verdict}</p>
        </div>
      </div>
      {assessment.keyFindings?.length > 0 && (
        <details className="qa-session-summary">
          <summary className="qa-session-summary__toggle">View full assessment details</summary>
          <div className="qa-session-summary__body">
            {assessment.recommendedMedications?.length > 0 && (
              <div className="qa-section">
                <h4 className="qa-section__title">Recommended Medications</h4>
                <div className="qa-med-list">
                  {assessment.recommendedMedications.map((med, i) => (
                    <MedCard
                      key={i}
                      med={med}
                      sources={assessment.sources}
                      uploadedFiles={session?.uploadedFiles}
                      onOpenPdf={handleOpenPdf}
                    />
                  ))}
                </div>
              </div>
            )}
            <div className="qa-section"><h4 className="qa-section__title">Key Findings</h4>
              <ul className="qa-findings">{assessment.keyFindings.map((f, i) => <li key={i} className="qa-findings__item">{f}</li>)}</ul>
            </div>
            {assessment.cautions?.length > 0 && (
              <div className="qa-section"><h4 className="qa-section__title qa-section__title--warn">⚠ Cautions</h4>
                <ul className="qa-findings qa-findings--caution">{assessment.cautions.map((c, i) => <li key={i} className="qa-findings__item">{c}</li>)}</ul>
              </div>
            )}
            {assessment.nextStep && <div className="qa-next-step"><span className="qa-next-step__label">Next Step</span><p className="qa-next-step__text">{assessment.nextStep}</p></div>}
          </div>
        </details>
      )}
      {/* Assessment-level sources — always visible outside the collapsible details block */}
      {assessment.sources?.length > 0 && (
        <ReferencesPanel
          sources={assessment.sources}
          uploadedFiles={session?.uploadedFiles}
          onOpenPdf={handleOpenPdf}
        />
      )}
      {/* ── Decision Sources — WHY approved/rejected ──────────────────────── */}
      <DecisionSourcesPanel
        reviewId={session?.clinicalReviewId}
        assessment={assessment}
        uploadedFiles={session?.uploadedFiles}
        onOpenPdf={handleOpenPdf}
        apiBaseUrl={ApiBaseUrl}
      />
      {session?.formAnswers && Object.keys(session.formAnswers).length > 0 ? (
        <FilledFormSummaryPanel
          formAnswers={session.formAnswers}
          serviceType={session.serviceType}
        />
      ) : (
        session?.uploadedFiles && <UploadedFilesPanel uploadedFileMeta={session.uploadedFiles} />
      )}
      {session?.formAnswers && session?.uploadedFiles?.bloodTestUrl && (
        <UploadedFilesPanel uploadedFileMeta={{ bloodTestUrl: session.uploadedFiles.bloodTestUrl, bloodTestName: session.uploadedFiles.bloodTestName }} />
      )}
      {/* Doctor's notes from when the session was saved */}
      <DoctorNotesPanel doctorContext={session?.doctorContext} />
      <div className="qa-chat-history">
        {chatHistory.map((msg, i) => (
          <div key={i} className={`qa-chat-msg qa-chat-msg--${msg.role}`}>
            {msg.role === "assistant" && <span className="qa-chat-msg__label">AI</span>}
            <div className="qa-chat-msg__bubble">
              {msg.role === "assistant" ? <FormatResponse text={msg.content} /> : <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{msg.content}</p>}
              {msg.role === "assistant" && msg.sources?.length > 0 && (
                <ReferencesPanel
                  sources={msg.sources}
                  uploadedFiles={session?.uploadedFiles}
                  onOpenPdf={handleOpenPdf}
                />
              )}
            </div>
          </div>
        ))}
        {(isChatLoading || extraFileUploading) && (
          <div className="qa-chat-msg qa-chat-msg--assistant">
            <span className="qa-chat-msg__label">AI</span>
            <div className="qa-chat-msg__bubble qa-chat-msg__bubble--thinking">
              <span className="qa-typing-dot" /><span className="qa-typing-dot" /><span className="qa-typing-dot" />
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>
      <div className="qa-chat-input-area">
        {extraFile && <div className="qa-chat-file-badge">📎 {extraFile.name}<button onClick={() => { setExtraFile(null); if (fileRef.current) fileRef.current.value = ""; }}>✕</button></div>}
        <div className="qa-chat-input-row">
          <textarea className="qa-chat-textarea" rows={2} placeholder="Ask a follow-up question about this patient..."
            value={message} onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }} />
          <button className="qa-chat-send-btn" onClick={handleSend} disabled={isChatLoading || extraFileUploading || (!message.trim() && !extraFile)}>
            {(isChatLoading || extraFileUploading) ? <span className="qa-spinner qa-spinner--dark" /> : "Send →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sessions Dropdown ─────────────────────────────────────────────────────────
function SessionDropdown({ sessions, selectedSessionId, onChange }) {
  if (!sessions || sessions.length === 0) return null;
  return (
    <div className="qa-session-bar">
      <label className="qa-label">Saved Assessment Sessions</label>
      <select className="qa-select" value={selectedSessionId} onChange={onChange}>
        <option value="">-- New Assessment --</option>
        {sessions.map((s) => <option key={s._id} value={s._id}>{s.sessionName}</option>)}
      </select>
    </div>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

// ── MedCard ───────────────────────────────────────────────────────────────────
// Renders a single medication card with:
//   - A "📋 Formulary" chip that expands the formulary entry inline
//   - A "📄 <Source>" chip per linked source that opens the PDF drawer
function MedCard({ med, sources, uploadedFiles, onOpenPdf }) {
  const [formularyOpen, setFormularyOpen] = useState(false);

  const getPdfUrl = (label) => resolvePdfUrl(label, uploadedFiles);

  const linkedSources = (med.source_indices || [])
    .map(i => (sources || [])[i])
    .filter(Boolean);

  return (
    <div className="qa-med-card">
      {/* Header: name + form badge */}
      <div className="qa-med-card__header">
        <span className="qa-med-card__name">{med.name}</span>
        <span className="qa-med-card__badge">{med.form}</span>
      </div>

      {/* Category badge */}
      {med.category && (
        <span className="qa-med-card__category">{med.category}</span>
      )}

      {/* Strength + volume */}
      <p className="qa-med-card__strength">
        {med.strength}{med.volume ? <span className="qa-med-card__volume"> · {med.volume}</span> : null}
      </p>

      {/* Rationale */}
      <p className="qa-med-card__rationale">{med.rationale}</p>

      {/* Medirecords status */}
      {med.inMedirecords === true && (
        <span className="qa-med-card__medirecords">✓ In Medirecords</span>
      )}
      {med.inMedirecords === false && (
        <span className="qa-med-card__medirecords qa-med-card__medirecords--no">✗ Not in Medirecords</span>
      )}

      {/* Reference chips */}
      <div className="qa-med-card__chips">
        {med.formulary_entry && (
          <button
            className={`qa-med-chip qa-med-chip--formulary${formularyOpen ? ' qa-med-chip--active' : ''}`}
            onClick={() => setFormularyOpen(o => !o)}
            title="Show formulary entry"
          >
            📋 Formulary {formularyOpen ? '▴' : '▾'}
          </button>
        )}
        {linkedSources.map((src, i) => {
          const pdfUrl = getPdfUrl(src.label);
          if (!pdfUrl || !onOpenPdf) return null;
          return (
            <button
              key={i}
              className="qa-med-chip qa-med-chip--pdf"
              onClick={() => onOpenPdf(pdfUrl, src.excerpt, src.verbatim_excerpt)}
              title={`View in ${src.label} PDF`}
            >
              📄 {src.label}
            </button>
          );
        })}
      </div>

      {/* Inline formulary expansion */}
      {formularyOpen && med.formulary_entry && (
        <div className="qa-med-formulary">
          <span className="qa-med-formulary__label">Bobbi Formulary</span>
          <span className="qa-med-formulary__entry">{med.formulary_entry}</span>
        </div>
      )}
    </div>
  );
}

export default function QuickAssessPanel({ projectId, projectName, onAssessmentComplete }) {
  const dispatch = useDispatch();
  const { sessions, currentSession, isSaving, isLoading } = useSelector((s) => s.assessmentSessions);

  const [surveyFile, setSurveyFile] = useState(null);
  const [bloodTestFile, setBloodTestFile] = useState(null);
  const [surveyMode, setSurveyMode] = useState("pdf");
  const [formAnswers, setFormAnswers] = useState({});
  const [serviceType, setServiceType] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [savedReviewId, setSavedReviewId] = useState(null);
  const [backendFileMeta, setBackendFileMeta] = useState(null);
  const [error, setError] = useState("");
  const [step, setStep] = useState("upload");
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [submittedFormAnswers, setSubmittedFormAnswers] = useState({});
  const [doctorContext, setDoctorContext] = useState("");

  const surveyRef = useRef();
  const bloodRef = useRef();

  // ── PDF Drawer state (live result view) ────────────────────────────────────
  const [pdfDrawerOpen, setPdfDrawerOpen] = useState(false);
  const [pdfDrawerUrl, setPdfDrawerUrl] = useState(null);
  const [pdfDrawerExcerpt, setPdfDrawerExcerpt] = useState(null);
  const [pdfDrawerVerbatim, setPdfDrawerVerbatim] = useState(null);

  const handleOpenPdf = (pdfUrl, excerpt, verbatimExcerpt) => {
    setPdfDrawerUrl(pdfUrl);
    setPdfDrawerExcerpt(excerpt);
    setPdfDrawerVerbatim(verbatimExcerpt || null);
    setPdfDrawerOpen(true);
  };
  const handleClosePdf = () => {
    setPdfDrawerOpen(false);
    setPdfDrawerUrl(null);
    setPdfDrawerExcerpt(null);
    setPdfDrawerVerbatim(null);
  };

  const SERVICE_OPTIONS = [
    { key: "low_testosterone_men", label: "Male TRT / Low Testosterone" },
    { key: "hair_loss", label: "Hair Loss" },
    { key: "hormone_health_women", label: "Female Hormones / HRT" },
    { key: "erectile_dysfunction", label: "Erectile Dysfunction" },
    { key: "weight_loss", label: "Weight Loss" },
    { key: "general", label: "General Clinical" },
  ];

  useEffect(() => {
    if (projectId) {
      dispatch(getAssessmentSessions({ projectId }));
      dispatch(clearCurrentSession());
      setSelectedSessionId("");
      setStep("upload");
      setResult(null);
      setSaveSuccess(false);
      setBackendFileMeta(null);
    }
  }, [projectId, dispatch]);

  const handleServiceTypeChange = (e) => {
    setServiceType(e.target.value);
    setSurveyMode("pdf");
    setFormAnswers({});
    setSurveyFile(null);
    if (surveyRef.current) surveyRef.current.value = "";
  };

  const handleSessionSelect = useCallback((e) => {
    const sid = e.target.value;
    setSelectedSessionId(sid);
    if (sid) { dispatch(loadAssessmentSession({ sessionId: sid })); setStep("session"); }
    else { dispatch(clearCurrentSession()); setStep("upload"); setResult(null); setBackendFileMeta(null); setSaveSuccess(false); }
  }, [dispatch]);

  const hasSurveyInput = surveyMode === "pdf" ? !!surveyFile : Object.keys(formAnswers).length > 0;
  const canRun = hasSurveyInput || !!bloodTestFile;

  const handleRun = async () => {
    if (!canRun) { setError("Please upload at least one document or fill in the intake form."); return; }
    if (!projectId) { alert("Please select a project."); return; }
    setError("");
    setLoading(true);

    try {
      let surveyText = "";
      let fileMeta = { surveyUrl: null, surveyName: null, bloodTestUrl: null, bloodTestName: null };
      let surveySource = "pdf";

      if (surveyMode === "form") {
        surveyText = serializeFormAnswers(INTAKE_QUESTIONS[serviceType] || [], formAnswers);
        surveySource = "form";
        setSubmittedFormAnswers({ ...formAnswers });
      } else if (surveyFile) {
        const uploaded = await uploadFilesToBackend(surveyFile, null);
        surveyText = uploaded.surveyPdf?.text || "";
        fileMeta.surveyUrl = uploaded.surveyPdf?.url || null;
        fileMeta.surveyName = uploaded.surveyPdf?.fileName || null;
      }

      let bloodTestText = "";
      if (bloodTestFile) {
        const uploadedBlood = await uploadFilesToBackend(null, bloodTestFile);
        bloodTestText = uploadedBlood.bloodTestPdf?.text || "";
        fileMeta.bloodTestUrl = uploadedBlood.bloodTestPdf?.url || null;
        fileMeta.bloodTestName = uploadedBlood.bloodTestPdf?.fileName || null;
      }

      const response = await axios.post(
        ApiBaseUrl + "/v1/user/quick-assess",
        { projectId, surveyText, bloodTestText, serviceType, doctorContext: doctorContext.trim() },
        { headers: { Authorization: localStorage.getItem("token") } }
      );

      if (response.data?.status === 1) {
        setResult(response.data.assessment);
        setSavedReviewId(response.data.reviewId);
        setBackendFileMeta({ ...fileMeta, surveySource });
        setStep("result");
        setSaveSuccess(false);
        if (onAssessmentComplete) onAssessmentComplete(response.data);
      } else {
        setError(response.data?.message || "Assessment failed. Please try again.");
      }
    } catch (err) {
      setError(err.message || "Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSurveyFile(null); setBloodTestFile(null); setSurveyMode("pdf"); setFormAnswers({});
    setResult(null); setError(""); setStep("upload"); setServiceType("");
    setSavedReviewId(null); setSaveSuccess(false); setSelectedSessionId(""); setBackendFileMeta(null);
    setSubmittedFormAnswers({});
    handleClosePdf();
    if (surveyRef.current) surveyRef.current.value = "";
    if (bloodRef.current) bloodRef.current.value = "";
  };

  const handleSaveConfirm = async (sessionName) => {
    const action = await dispatch(saveAssessmentSession({ projectId, clinicalReviewId: savedReviewId, sessionName, assessmentResult: result, serviceType, uploadedFiles: backendFileMeta || {}, formAnswers: submittedFormAnswers && Object.keys(submittedFormAnswers).length > 0 ? submittedFormAnswers : null, doctorContext: doctorContext.trim() || "" }));
    if (action?.payload?.status === 1) { setShowSaveModal(false); setSaveSuccess(true); dispatch(getAssessmentSessions({ projectId })); }
  };

  if (step === "session") {
    return (
      <div className="qa-panel">
        <SessionDropdown sessions={sessions} selectedSessionId={selectedSessionId} onChange={handleSessionSelect} />
        {isLoading || !currentSession ? (
          <div className="qa-loading-session"><span className="qa-spinner qa-spinner--blue" /> Loading session...</div>
        ) : <SessionChatView session={currentSession} />}
      </div>
    );
  }

  if (step === "result" && result) {
    return (
      <>
        {showSaveModal && <SaveModal onSave={handleSaveConfirm} onCancel={() => setShowSaveModal(false)} isSaving={isSaving} />}

        {/* ── PDF Reference Drawer (reused from AI Chat) ─────────────────── */}
        <AssessmentPdfDrawer
          isOpen={pdfDrawerOpen}
          onClose={handleClosePdf}
          pdfUrl={pdfDrawerUrl}
          excerpt={pdfDrawerExcerpt}
          verbatimExcerpt={pdfDrawerVerbatim}
        />

        <div className="qa-panel qa-panel--result">
          <SessionDropdown sessions={sessions} selectedSessionId={selectedSessionId} onChange={handleSessionSelect} />
          <div className={`qa-verdict qa-verdict--${result.eligible ? "yes" : "no"}`}>
            <span className="qa-verdict__icon">{result.eligible ? "✓" : "✕"}</span>
            <div className="qa-verdict__text">
              <span className="qa-verdict__label">{result.eligible ? "ELIGIBLE FOR TREATMENT" : "NOT ELIGIBLE"}</span>
              <p className="qa-verdict__reason">{result.verdict}</p>
            </div>
          </div>
          {result.eligible && result.recommendedMedications?.length > 0 && (
            <div className="qa-section"><h4 className="qa-section__title">Recommended Medications</h4>
              <div className="qa-med-list">{result.recommendedMedications.map((med, i) => (
                <MedCard
                  key={i}
                  med={med}
                  sources={result.sources}
                  uploadedFiles={backendFileMeta}
                  onOpenPdf={handleOpenPdf}
                />
              ))}</div>
            </div>
          )}
          {result.keyFindings?.length > 0 && (
            <div className="qa-section"><h4 className="qa-section__title">Key Findings</h4>
              <ul className="qa-findings">{result.keyFindings.map((f, i) => <li key={i} className="qa-findings__item">{f}</li>)}</ul>
            </div>
          )}
          {result.cautions?.length > 0 && (
            <div className="qa-section"><h4 className="qa-section__title qa-section__title--warn">⚠ Cautions</h4>
              <ul className="qa-findings qa-findings--caution">{result.cautions.map((c, i) => <li key={i} className="qa-findings__item">{c}</li>)}</ul>
            </div>
          )}
          {result.nextStep && (
            <div className="qa-next-step"><span className="qa-next-step__label">Next Step</span>
              <p className="qa-next-step__text">{result.nextStep}</p>
              {result.requiresBloodTest && <p className="qa-blood-warning">🩸 Blood test required before prescribing.</p>}
            </div>
          )}

          {/* ── Decision Sources — WHY approved/rejected ─────────────────── */}
          <DecisionSourcesPanel
            reviewId={savedReviewId}
            assessment={result}
            uploadedFiles={backendFileMeta}
            onOpenPdf={handleOpenPdf}
            apiBaseUrl={ApiBaseUrl}
          />

          {/* Form source: show Q&A summary. PDF source: show document links */}
          {backendFileMeta?.surveySource === "form" && Object.keys(submittedFormAnswers).length > 0 ? (
            <FilledFormSummaryPanel
              formAnswers={submittedFormAnswers}
              serviceType={serviceType}
            />
          ) : (
            backendFileMeta && <UploadedFilesPanel uploadedFileMeta={backendFileMeta} />
          )}

          {/* Always show blood test link if present (even when survey was a form) */}
          {backendFileMeta?.surveySource === "form" && backendFileMeta?.bloodTestUrl && (
            <UploadedFilesPanel uploadedFileMeta={{ bloodTestUrl: backendFileMeta.bloodTestUrl, bloodTestName: backendFileMeta.bloodTestName }} />
          )}

          {/* Doctor's notes preview */}
          <DoctorNotesPanel doctorContext={doctorContext} />

          <p className="qa-disclaimer">AI-assisted decision support only. All recommendations must be reviewed and approved by a licensed clinician before being acted upon.</p>

          {/* Sources with PDF drawer — passes uploadedFiles so "View in PDF" buttons know which file to open */}
          {result.sources?.length > 0 && (
            <ReferencesPanel
              sources={result.sources}
              uploadedFiles={backendFileMeta}
              onOpenPdf={handleOpenPdf}
            />
          )}

          {saveSuccess && <div className="qa-save-success">Session saved! Find it in the <strong>Saved Assessment Sessions</strong> dropdown above.</div>}
          <div className="qa-actions">
            <button className="qa-btn qa-btn--secondary" onClick={handleReset}>Assess Another Patient</button>
            {!saveSuccess && <button className="qa-btn qa-btn--save" onClick={() => setShowSaveModal(true)}>Save Session</button>}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="qa-panel">
      <SessionDropdown sessions={sessions} selectedSessionId={selectedSessionId} onChange={handleSessionSelect} />
      <div className="qa-header">
        <div>
          <h3 className="qa-header__title">Quick Patient Assessment</h3>
          <p className="qa-header__sub">Upload the patient's survey and/or blood test PDF — AI will instantly assess eligibility against the Bobbi formulary.{" "}
            <strong>No patient names or PII are sent to the AI.</strong></p>
        </div>
      </div>

      <div className="qa-field">
        <label className="qa-label">Service / Treatment Category</label>
        <select className="qa-select" value={serviceType} onChange={handleServiceTypeChange}>
          <option value="">— Select a category —</option>
          {SERVICE_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      {!serviceType ? (
        <div className="qa-no-category"><p>Please select a Service / Treatment Category above to continue.</p></div>
      ) : (
        <>
          <div className="qa-uploads">
            <SurveyInputSection
              surveyFile={surveyFile}
              setSurveyFile={setSurveyFile}
              surveyMode={surveyMode}
              setSurveyMode={setSurveyMode}
              formAnswers={formAnswers}
              setFormAnswers={setFormAnswers}
              serviceType={serviceType}
              surveyRef={surveyRef}
            />
            <div className={`qa-dropzone ${bloodTestFile ? "qa-dropzone--filled" : ""}`} onClick={() => bloodRef.current?.click()}>
              <input ref={bloodRef} type="file" accept=".pdf,.doc,.docx" style={{ display: "none" }}
                onChange={(e) => setBloodTestFile(e.target.files[0] || null)} />
              {bloodTestFile ? (
                <>
                  <span className="qa-dropzone__check">✓</span>
                  <p className="qa-dropzone__name">{bloodTestFile.name}</p>
                  <button className="qa-dropzone__remove" onClick={(e) => { e.stopPropagation(); setBloodTestFile(null); if (bloodRef.current) bloodRef.current.value = ""; }}>✕</button>
                </>
              ) : (
                <>
                  <span className="qa-dropzone__icon">🩸</span>
                  <p className="qa-dropzone__title">Test Results PDF</p>
                  <p className="qa-dropzone__hint">Click to upload pathology / lab results</p>
                </>
              )}
            </div>
          </div>

          {error && <p className="qa-error">{error}</p>}

          {/* ── Doctor Context Textbox ─────────────────────────────────────── */}
          <div className="qa-doctor-context">
            <label className="qa-label qa-label--context">
              Additional Clinical Context
              <span className="qa-label__optional"> (optional)</span>
            </label>
            <p className="qa-context-hint">
              Add any further observations, recent history, or clinical notes to help guide the AI assessment. Maximum 500 words.
            </p>
            <div className="qa-context-wrapper">
              <textarea
                className="qa-context-textarea"
                rows={5}
                placeholder="e.g. Patient has recently reported worsening fatigue over 3 months. Previous testosterone result from 6 months ago was borderline. Family history of cardiovascular disease. Currently on metformin for pre-diabetes..."
                value={doctorContext}
                onChange={(e) => {
                  const words = e.target.value.trim().split(/\s+/).filter(Boolean).length;
                  if (words <= 500 || e.target.value.length < doctorContext.length) {
                    setDoctorContext(e.target.value);
                  }
                }}
                maxLength={3500}
              />
              <div className={`qa-context-counter ${doctorContext.trim().split(/\s+/).filter(Boolean).length > 450 ? "qa-context-counter--warn" : ""}`}>
                {doctorContext.trim() === "" ? 0 : doctorContext.trim().split(/\s+/).filter(Boolean).length} / 500 words
              </div>
            </div>
          </div>

          <button className="qa-btn qa-btn--primary" onClick={handleRun} disabled={loading || !canRun}>
            {loading ? <span className="qa-btn__loading"><span className="qa-spinner" /> Uploading & Analysing...</span> : "Run Assessment →"}
          </button>
          <p className="qa-privacy-note">🔒 Patient names, dates of birth, addresses, and contact details are automatically removed before documents are processed by the AI.</p>
        </>
      )}
    </div>
  );
}